import { ReactNode } from 'react';
import { briefIsShowable, useMorningBriefQuery } from '../../../lib/brief';
import { isBriefPulsePaused, resolveBriefState } from '../../../lib/brief-state';
import { usePulses } from '../../../lib/pulses';
import { useWorkspaces } from '../../../lib/workspaces';
import { useNarrow } from '../../../lib/use-narrow';
import { Card } from '../ui';
import { MaestroBrief, MobileMaestroBrief } from '../MaestroBrief';
import { ActivateBrief } from './ActivateBrief';
import { MobileActivateBrief } from './MobileActivateBrief';
import { MobileNewWorkspaceBrief } from './MobileNewWorkspaceBrief';
import { NewWorkspaceBrief } from './NewWorkspaceBrief';

// The catch-up header's morning-brief slot — one of three states, picked by `resolveBriefState`
// (the native apps' decision, ported in lib/brief-state). Figma "Catch Up states" 7419:47452.
//
// The redesign puts the desktop brief inside a white DS Card at ~⅔ width beside the QR (the "card with
// buttons" — see `DesktopHomeHeader`). The mobile variants are already floating cards of their own
// (`MobileMaestroBrief` etc.), so on narrow the state renders bare.

export function BriefSlot() {
  const { brief, settled: briefSettled } = useMorningBriefQuery();
  const { state: pulses } = usePulses();
  const workspaces = useWorkspaces();
  const narrow = useNarrow();

  const state = resolveBriefState({
    pulsesLoaded: pulses.kind === 'ready',
    pulsePaused: pulses.kind === 'ready' && isBriefPulsePaused(pulses.pulses),
    briefSettled,
    // A brief is "there" for the hero if it's showable — a finished brief with content, OR one still in
    // motion (created today, convening). A failed or stale brief is NOT, so the slot falls back to its
    // other states rather than to a prior day's brief.
    hasBrief: briefIsShowable(brief),
    hasWorkspace: workspaces.status === 'ready' && workspaces.workspaces.length > 0,
    // Until the workspaces fetch settles we can't tell newWorkspace from none for a never-seeded
    // routine, so hold `loading` rather than flashing nothing then the card.
    workspacesSettled: workspaces.status === 'ready',
  });

  const firstWorkspace = workspaces.status === 'ready' ? workspaces.workspaces[0] : undefined;

  let content: ReactNode = null;
  switch (state) {
    case 'hero':
      content = narrow ? <MobileMaestroBrief /> : <MaestroBrief />;
      break;
    case 'newWorkspace':
      content = narrow ? (
        <MobileNewWorkspaceBrief workspace={firstWorkspace} />
      ) : (
        <NewWorkspaceBrief workspace={firstWorkspace} />
      );
      break;
    case 'activate':
      content = narrow ? <MobileActivateBrief /> : <ActivateBrief />;
      break;
    default:
      return null;
  }

  if (narrow) return <>{content}</>;
  return <Card>{content}</Card>;
}
